import React, { useState } from 'react';
import {
  Typography,
  IconButton,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import CloseIcon from '@mui/icons-material/Close';
import { COLOR_PRIMARY_2 } from 'src/utils/colors';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';

const HeatmapLegendBox = ({
  metricsKey,
  hideData,
  neighbourhoodAnalysisKey,
  neighbourhoodChoroplethLegends,
  loading,
  additionalStyle = {},
  defaultExpanded = true,
  legendMap = {
    revenue: 'Revenue',
    occupancy: 'Occupancy Rate',
    listing: 'Supply',
  } as Record<string, string>,
  isLocked,
  handleOnSampleClick,
  showSubmarketMobileLegend,
  setShowSubmarketMobileLegend,
}: {
  metricsKey?: string;
  hideData: boolean;
  neighbourhoodAnalysisKey: string;
  neighbourhoodChoroplethLegends: Record<string, string>;
  loading: boolean;
  additionalStyle?: React.CSSProperties;
  defaultExpanded?: boolean;
  legendMap?: Record<string, string>;
  isLocked?: boolean;
  handleOnSampleClick?: (plan: string) => void;
  showSubmarketMobileLegend: boolean;
  setShowSubmarketMobileLegend: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(900));

  if (hideData) return <></>;

  const legendContent = (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        my: 2,
        flexWrap: 'wrap',
        gap: 2,
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        Legend {metricsKey && <>({legendMap[metricsKey]})</>}
      </Typography>

      {isExpanded && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {Object.keys(neighbourhoodChoroplethLegends)
            .reverse()
            .map(breakpoint => {
              if (!breakpoint.includes('undefined')) {
                return (
                  <Box key={breakpoint} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <div
                      style={{
                        width: '16px',
                        height: '16px',
                        background: neighbourhoodChoroplethLegends[breakpoint],
                        borderRadius: '2px',
                        border: '1px solid rgba(110, 110, 110, 0.32)',
                      }}
                    />
                    <Typography variant="body2" sx={{ fontSize: '14px' }}>
                      {isLocked ? (
                        <LockIcon
                          onClick={() => handleOnSampleClick && handleOnSampleClick('expert')}
                          sx={{
                            fontSize: 18,
                            color: COLOR_PRIMARY_2,
                            mr: 1,
                            cursor: 'pointer',
                            transform: 'translateY(2px)',
                          }}
                        />
                      ) : (
                        breakpoint
                      )}
                    </Typography>
                  </Box>
                );
              }
              return null;
            })}
        </Box>
      )}
    </Box>
  );

  if (isMobile) {
    return (
      <>
        <Dialog
          open={showSubmarketMobileLegend}
          onClose={() => setShowSubmarketMobileLegend(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '10px',
              boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.15)',
            },
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              pb: 1,
            }}
          >
            <IconButton
              onClick={() => setShowSubmarketMobileLegend(false)}
              size="small"
              sx={{ minWidth: 'auto', p: 0.5 }}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>{legendContent}</DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <Box
      style={{
        width: isExpanded ? 'auto' : '100px',
        padding: isExpanded ? '10px 15px' : '5px 15px',
        background: 'white',
        boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.15)',
        borderRadius: '10px',
        zIndex: 3,
        transition: 'all 0.3s ease',
        pointerEvents: 'auto',
        ...additionalStyle,
      }}
    >
      {legendContent}
    </Box>
  );
};

export default HeatmapLegendBox;
